import * as Api from '@ViewModels';
import { UseQueryResult } from '@tanstack/react-query';
import { CampaignType } from '../../../../models/AdminModels';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import {
	useBlogCardsAllCategoryTemplates,
	useBlogCardsCategories,
	useBlogCardsCategoryTemplates,
	useBlogsCardSearchTemplates,
	useEmailAllCategoryTemplates,
	useEmailCategoryTemplates,
	useEmailSearchTemplates,
	useMyTemplatesQuery,
	useSocialAllCategoryTemplates,
	useSocialCategories,
	useSocialSearchTemplates,
	useTemplateCategorySocialQuery,
	useTemplateIndustryCategoriesQuery,
} from '../../../../queries';
import { useSocialDrafts } from '../../../../queries/SocialCampaign/useSocialDrafts';
import { useBlogDrafts } from '../../../../queries/Templates/useBlogDrafts';
import { ITemplateCard, KnownCategories } from '../../../../viewmodels/AppViewModels';

export type ContentCalendarTemplateBrowserParams = {
	impersonationContext?: Api.IImpersonationContext;
	templateType?: CampaignType;
	searchQuery?: string;
	industry?: Api.Industry;
	category?: string;
	selectedUserId?: string | null;
	forUserId?: string | null;
};
const categoriesWithOwnQueries = [
	KnownCategories.All,
	KnownCategories.MyTemplates,
	KnownCategories.SocialMediaDrafts,
	KnownCategories.BlogPostDrafts,
];

export function useSocialMediaTemplates({
	templateType,
	impersonationContext,
	category,
	industry,
	searchQuery,
	selectedUserId,
	forUserId,
}: ContentCalendarTemplateBrowserParams) {
	const userSession = useUserSession();
	const emailTemplateTypeSelected = templateType === CampaignType.Social;
	const account = impersonationContext?.account?.id ? impersonationContext.account : userSession?.account;
	const socialMediaEnabled = account?.features?.socialMedia.enabled;
	const isSearching = Boolean(searchQuery);

	const templatesByIndustryAndCategoryEnabled =
		socialMediaEnabled &&
		emailTemplateTypeSelected &&
		Boolean(category) &&
		Boolean(industry) &&
		category !== KnownCategories.All &&
		!isSearching;
	const socialTemplatesQuery = useTemplateCategorySocialQuery({
		industry,
		categoryName: category === KnownCategories.MyTemplates ? 'me' : category,
		enabled: templatesByIndustryAndCategoryEnabled,
		impersonationContext,
	});

	const categoriesEnabled = socialMediaEnabled && emailTemplateTypeSelected && Boolean(industry);
	const socialTemplateCategoriesQuery = useSocialCategories({
		industry,
		impersonationContext,
		enabled: categoriesEnabled,
		select: categories => {
			return categories.filter(
				c => c !== 'Archived' && c !== 'Uncategorized' && c !== 'HTML Newsletters' && c !== 'My Templates'
			);
		},
		withDrafts: account?.features?.socialMedia.enabled,
	});

	const browseEnabled =
		socialMediaEnabled && emailTemplateTypeSelected && category === KnownCategories.All && !isSearching;
	const allSocialTemplatesQuery = useSocialAllCategoryTemplates({
		enabled: browseEnabled,
		industry,
		impersonationContext,
	});

	const searchEnabled = socialMediaEnabled && emailTemplateTypeSelected && isSearching;
	const socialTemplateSearchQuery = useSocialSearchTemplates({
		impersonationContext,
		query: searchQuery,
		industry,
		enabled: searchEnabled,
	});

	const draftsEnabled =
		socialMediaEnabled && emailTemplateTypeSelected && category === KnownCategories.SocialMediaDrafts && !isSearching;
	const socialDraftsQuery = useSocialDrafts({
		enabled: draftsEnabled,
		impersonationContext,
		forUserId: forUserId || selectedUserId,
	});

	const myTemplatesEnabled =
		socialMediaEnabled && emailTemplateTypeSelected && category === KnownCategories.MyTemplates && !isSearching;
	const mySocialTemplatesQuery = useMyTemplatesQuery<ITemplateCard>({
		impersonationContext,
		industry,
		enabled: myTemplatesEnabled,
		templateType: Api.TemplateType.SocialMediaPost,
	});

	const socialTemplatesLoading =
		(searchEnabled && socialTemplateSearchQuery.isPending) ||
		(browseEnabled && allSocialTemplatesQuery.isPending) ||
		(templatesByIndustryAndCategoryEnabled && socialTemplatesQuery.isPending) ||
		(categoriesEnabled && socialTemplateCategoriesQuery.isPending) ||
		(draftsEnabled && socialDraftsQuery.isPending) ||
		(myTemplatesEnabled && mySocialTemplatesQuery.isPending);

	return {
		socialTemplatesLoading,
		socialTemplateCategoriesQuery,
		socialTemplatesQuery,
		socialTemplateSearchQuery,
		allSocialTemplatesQuery,
		socialDraftsQuery,
		mySocialTemplatesQuery,
	};
}

export function useEmailTemplates({
	templateType,
	impersonationContext,
	category,
	industry,
	searchQuery,
}: ContentCalendarTemplateBrowserParams) {
	const emailTemplateTypeSelected = templateType === CampaignType.Email;
	const isSearching = Boolean(searchQuery);

	const templatesByIndustryAndCategoryEnabled =
		emailTemplateTypeSelected &&
		Boolean(category) &&
		Boolean(industry) &&
		!categoriesWithOwnQueries.includes(category) &&
		!isSearching;
	const emailTemplatesQuery = useEmailCategoryTemplates({
		industry,
		categoryName: category,
		enabled: templatesByIndustryAndCategoryEnabled,
		impersonationContext,
		// We don't want to retry if the category doesn't exist
		retry: (_, error) => error.systemCode !== 404,
	});

	const categoriesEnabled = emailTemplateTypeSelected && Boolean(industry);
	const emailTemplateCategoriesQuery = useTemplateIndustryCategoriesQuery({
		industry,
		impersonationContext,
		enabled: categoriesEnabled,
	});

	const browseEnabled = emailTemplateTypeSelected && category === KnownCategories.All && !isSearching;
	const allEmailTemplatesQuery = useEmailAllCategoryTemplates({
		enabled: browseEnabled,
		industry,
		impersonationContext,
	});

	const searchEnabled = emailTemplateTypeSelected && isSearching;
	const emailTemplateSearchQuery = useEmailSearchTemplates({
		impersonationContext,
		query: searchQuery,
		industry,
		enabled: searchEnabled,
	});

	const myTemplatesEnabled = emailTemplateTypeSelected && category === KnownCategories.MyTemplates && !isSearching;
	const myEmailTemplatesQuery = useMyTemplatesQuery<ITemplateCard>({
		impersonationContext,
		industry,
		enabled: myTemplatesEnabled,
	});

	const emailTemplatesLoading =
		(searchEnabled && emailTemplateSearchQuery.isPending) ||
		(browseEnabled && allEmailTemplatesQuery.isPending) ||
		(templatesByIndustryAndCategoryEnabled && emailTemplatesQuery.isPending) ||
		(categoriesEnabled && emailTemplateCategoriesQuery.isPending) ||
		(myTemplatesEnabled && myEmailTemplatesQuery.isPending);

	return {
		myEmailTemplatesQuery,
		emailTemplatesLoading,
		emailTemplateCategoriesQuery,
		emailTemplatesQuery,
		emailTemplateSearchQuery,
		allEmailTemplatesQuery,
	};
}

export function useBloggingTemplates({
	templateType,
	impersonationContext,
	category,
	industry,
	searchQuery,
	selectedUserId,
	forUserId,
}: ContentCalendarTemplateBrowserParams) {
	const userSession = useUserSession();
	const account = impersonationContext?.account?.id ? impersonationContext.account : userSession?.account;
	const blogsEnabled = account?.features?.blogFeature.enabled;
	const blogsTemplateTypeSelected = templateType === CampaignType.Blog;
	const isSearching = Boolean(searchQuery);

	const templatesByIndustryAndCategoryEnabled =
		blogsTemplateTypeSelected &&
		blogsEnabled &&
		Boolean(category) &&
		Boolean(industry) &&
		!categoriesWithOwnQueries.includes(category) &&
		!isSearching;
	const blogTemplatesQuery = useBlogCardsCategoryTemplates({
		impersonationContext,
		category,
		enabled: templatesByIndustryAndCategoryEnabled,
		industry,
		// We don't want to retry if the category doesn't exist
		retry: (_, error) => error.systemCode !== 404,
	});

	const categoriesEnabled = blogsTemplateTypeSelected && blogsEnabled && Boolean(industry);
	const blogTemplateCategoriesQuery = useBlogCardsCategories({
		enabled: categoriesEnabled,
		impersonationContext,
		industry,
		withAll: false,
		withMyTemplates: false,
		withDrafts: account?.features?.blogFeature.enabled,
	});

	const browseEnabled = blogsTemplateTypeSelected && blogsEnabled && category === KnownCategories.All && !isSearching;
	const allBlogTemplatesQuery = useBlogCardsAllCategoryTemplates({
		enabled: browseEnabled,
		industry,
		impersonationContext,
	});

	const searchEnabled = blogsTemplateTypeSelected && blogsEnabled && isSearching;
	const blogSearchQuery = useBlogsCardSearchTemplates({
		impersonationContext,
		search: searchQuery,
		industry,
		enabled: searchEnabled,
	});

	const draftsEnabled =
		blogsEnabled && blogsTemplateTypeSelected && category === KnownCategories.BlogPostDrafts && !isSearching;
	const blogDraftsQuery = useBlogDrafts({
		enabled: draftsEnabled,
		impersonationContext,
		forUserId: forUserId || selectedUserId,
	});

	const blogTemplatesLoading =
		(searchEnabled && blogSearchQuery.isPending) ||
		(browseEnabled && allBlogTemplatesQuery.isPending) ||
		(templatesByIndustryAndCategoryEnabled && blogTemplatesQuery.isPending) ||
		(categoriesEnabled && blogTemplateCategoriesQuery.isPending) ||
		(draftsEnabled && blogDraftsQuery.isPending);
	return {
		blogSearchQuery,
		allBlogTemplatesQuery,
		blogTemplatesQuery,
		blogTemplateCategoriesQuery,
		blogTemplatesLoading,
		blogDraftsQuery,
	};
}

export function useContentCalendarTemplateBrowserQueries({
	templateType,
	impersonationContext,
	category,
	industry,
	searchQuery,
	selectedUserId,
	forUserId,
}: ContentCalendarTemplateBrowserParams) {
	const {
		socialTemplatesLoading,
		socialTemplateCategoriesQuery,
		socialTemplatesQuery,
		socialTemplateSearchQuery,
		allSocialTemplatesQuery,
		socialDraftsQuery,
		mySocialTemplatesQuery,
	} = useSocialMediaTemplates({
		templateType,
		searchQuery,
		category,
		industry,
		impersonationContext,
		selectedUserId,
		forUserId,
	});

	const {
		blogTemplatesQuery,
		blogTemplatesLoading,
		blogTemplateCategoriesQuery,
		allBlogTemplatesQuery,
		blogSearchQuery,
		blogDraftsQuery,
	} = useBloggingTemplates({
		templateType,
		searchQuery,
		category,
		industry,
		impersonationContext,
		selectedUserId,
		forUserId,
	});

	const {
		emailTemplateCategoriesQuery,
		allEmailTemplatesQuery,
		emailTemplateSearchQuery,
		emailTemplatesQuery,
		myEmailTemplatesQuery,
		emailTemplatesLoading,
	} = useEmailTemplates({
		templateType,
		searchQuery,
		category,
		industry,
		impersonationContext,
	});

	const isLoading = blogTemplatesLoading || emailTemplatesLoading || socialTemplatesLoading;
	let templatesByCategoryQuery: UseQueryResult<Api.IDictionary<ITemplateCard[]>, Error>;
	let myTemplatesQuery: UseQueryResult<ITemplateCard[], Error>;
	let templatesQuery: UseQueryResult<ITemplateCard[], Api.IOperationResultNoValue>;
	let templateSearchQuery: UseQueryResult<ITemplateCard[], Error>;
	let templateCategoriesQuery: UseQueryResult<string[], any>;

	switch (templateType) {
		case CampaignType.Email: {
			templatesByCategoryQuery = allEmailTemplatesQuery;
			myTemplatesQuery = myEmailTemplatesQuery;
			templatesQuery = emailTemplatesQuery;
			templateSearchQuery = emailTemplateSearchQuery;
			templateCategoriesQuery = emailTemplateCategoriesQuery;
			break;
		}
		case CampaignType.Social: {
			templatesByCategoryQuery = allSocialTemplatesQuery;
			// Use drafts query for SocialMediaDrafts, My Templates query for MyTemplates
			myTemplatesQuery = category === KnownCategories.SocialMediaDrafts ? socialDraftsQuery : mySocialTemplatesQuery;
			templatesQuery = socialTemplatesQuery;
			templateSearchQuery = socialTemplateSearchQuery;
			templateCategoriesQuery = socialTemplateCategoriesQuery;
			break;
		}
		case CampaignType.Blog: {
			templatesByCategoryQuery = allBlogTemplatesQuery;
			myTemplatesQuery = blogDraftsQuery;
			templatesQuery = blogTemplatesQuery;
			templateSearchQuery = blogSearchQuery;
			templateCategoriesQuery = blogTemplateCategoriesQuery;
			break;
		}
		default: {
			break;
		}
	}

	return {
		isLoading,
		templatesByCategoryQuery,
		myTemplatesQuery,
		templatesQuery,
		templateSearchQuery,
		templateCategoriesQuery,
	};
}
